import { Flex, Form } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { ThunderboltOutlined } from "@/icons"
import { useActionsOptionsWorkflow } from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import { ButtonListField } from "@/pages/GovernancePage/components/molecules/ButtonListField"
import { getWritePermission } from "@/pages/GovernancePage/helpers/workflows"
import { SectionFormTemplate } from "@/pages/SettingsPage/components/templates/SectionFormTemplate"
import { ActionCard } from "./ActionCard"

import { ActionsSectionFormProps } from "./ActionsSectionForm.types"

const processAvailableOptions = (
  options: any[],
  selectedActions: { type: string }[]
) => {
  const selectedActionTypes = selectedActions.reduce(
    (acc, { type }) => {
      if (type) acc[type] = true
      return acc
    },
    {} as Record<string, boolean>
  )
  // Check if dismiss or reopen actions are selected
  const hasDismissAction = selectedActionTypes["dismiss"]

  const hasReopenAction = selectedActionTypes["reopen"]

  let allOptionsDisabled = true

  const processedOptions = options.reduce(
    (acc, { disabled: optionDisabled, ...option }) => {
      // Check if the option is already selected or originally disabled
      let nextDisabled = optionDisabled || !!selectedActionTypes[option.key]

      // Apply the status action exclusion rules
      if (option.key === "dismiss" && hasReopenAction) {
        nextDisabled = true
      } else if (option.key === "reopen" && hasDismissAction) {
        nextDisabled = true
      }

      acc.push({ disabled: nextDisabled, ...option })

      // Update allOptionsDisabled if we found at least one enabled option
      if (allOptionsDisabled && !nextDisabled) {
        allOptionsDisabled = false
      }

      return acc
    },
    [] as { disabled: boolean }[]
  )

  return { processedOptions, allOptionsDisabled }
}

export const ActionsSectionForm: React.FC<ActionsSectionFormProps> = ({
  disabled,
  form,
  triggerCategory,
  triggerType
}) => {
  const { t } = useTranslation()
  const keyPrefix = "settings.automatedActions.form"

  const { response: optionsResponse } = useActionsOptionsWorkflow(
    { trigger_type: triggerType, trigger_category: triggerCategory },
    { enabled: !disabled }
  )
  const { data: options = [] } = optionsResponse || {}

  const actions = (Form.useWatch("actions", form) as { type: string }[]) || []

  // Process available options using our extracted function
  const { processedOptions: availableOptions, allOptionsDisabled } =
    processAvailableOptions(options, actions)

  const selectNoAction = actions.length === 0
  const maxActionNumber = actions.length === options.length

  const { hasPermission } = useUser()
  const userHasWritePermission = hasPermission(getWritePermission())
  const disabledButton =
    allOptionsDisabled ||
    selectNoAction ||
    maxActionNumber ||
    !userHasWritePermission

  return (
    <SectionFormTemplate
      withMargin
      id="actions"
      title={t(`${keyPrefix}.sections.actions`)}
      Icon={ThunderboltOutlined}
    >
      <Form.List
        name="actions"
        initialValue={[{ type: undefined, endpoint: undefined }]}
      >
        {(fields, { add, remove }) => (
          <Flex vertical gap={16}>
            {fields.map((field, index) => (
              <ActionCard
                key={field.key}
                field={field}
                form={form}
                options={availableOptions}
                disabled={disabled || !userHasWritePermission}
                t={t}
                onRemove={fields.length > 1 ? () => remove(index) : undefined}
                keyPrefix={keyPrefix}
                index={index}
                triggerType={triggerType}
              />
            ))}
            {
              <ButtonListField
                type="and"
                onClick={() => add({ type: undefined, endpoint: undefined })}
                disabled={disabledButton}
              >
                {t(`${keyPrefix}.fields.action.add`)}
              </ButtonListField>
            }
          </Flex>
        )}
      </Form.List>
    </SectionFormTemplate>
  )
}
